import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingRecordDetailsCustomer, OrderLineRecordCustomer, PaymentOption } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { NoddiInfoBanner } from 'noddi-ui-web';
import {
  formatCurrencyAmount,
  formatLicensePlate,
  getCarDisplayName,
  getIsPayAfterAwaitingCompletion,
  getJobCompletedAndPaymentLeft,
  groupDiscountsByOrderLine
} from 'noddi-util';
import { DiscountLines, OrderLinePrice, useAuthContext } from 'noddi-web';
import { RefObject } from 'react';
import { useMembershipProgramImages } from '../../hooks/useMembershipProgramImages';
import { buildInvoiceText } from '../../utils/invoiceText';
import { InitiatePaymentSessionButton } from './InitiatePaymentSessionButton';

interface BookingPaymentSummaryProps {
  booking: BookingRecordDetailsCustomer;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

const isOrderLineListedUnderVehicle = (line: OrderLineRecordCustomer) => !line.isDeliveryFee;

export const BookingPaymentSummary = ({ booking, paymentButtonRef }: BookingPaymentSummaryProps) => {
  const { order, bookingItemsCar, deliveryWindowFee, userGroup, serviceOrganizationId } = booking;
  const { isLoggedIn, currentUserGroupId } = useAuthContext();

  const isJobCompletedAndPaymentLeft = getJobCompletedAndPaymentLeft(booking);
  const isPayAfterAwaitingCompletion = getIsPayAfterAwaitingCompletion(booking);
  const isInvoice = order.paymentOption === PaymentOption.INVOICE;
  const hasPartialPayment = order.amountPaid.amount > 0 && order.amountOutstanding.amount > 0;
  const isRefundDue = order.amountOutstanding.amount < 0;

  const discountsByLine = groupDiscountsByOrderLine(order.discounts ?? []);
  const { imageByDiscountName } = useMembershipProgramImages();

  const organizationName = order.customer.organizationName ?? userGroup.name;
  const licensePlateNumber = bookingItemsCar[0]?.licensePlate.number;
  const lookupUserGroupId = currentUserGroupId ?? userGroup.id;

  // Enrich invoice banner with org registration number when available.
  // Low-stakes: fall back to name-only banner if lookup fails.
  const { data: organizationsForCar, error: _organizationsForCarError } = noddiAsync.useGet({
    type: URLKeys.getOrganizationsForCar,
    input: {
      licensePlateNumber: licensePlateNumber!,
      serviceOrganizationIds: [serviceOrganizationId],
      userGroupId: lookupUserGroupId
    },
    queryConfig: {
      enabled: isInvoice && isLoggedIn && !!licensePlateNumber && !!lookupUserGroupId
    }
  });

  const registrationNumber =
    organizationsForCar?.find((org) => org.userGroupId === userGroup.id)?.registrationNumber ??
    organizationsForCar?.find((org) => org.organizationName === organizationName)?.registrationNumber ??
    organizationsForCar?.[0]?.registrationNumber ??
    null;

  const invoiceBannerText = buildInvoiceText(organizationName, registrationNumber);

  const orderLines = order.orderLines.filter(isOrderLineListedUnderVehicle);
  const withdrawnOrderLines = order.withdrawnOrderLines.filter(isOrderLineListedUnderVehicle);
  const [onlyCar] = bookingItemsCar;
  const groupedCars =
    onlyCar != null && bookingItemsCar.length === 1
      ? [{ car: onlyCar, orderLines, withdrawnOrderLines }]
      : bookingItemsCar.map((car) => ({
          car,
          orderLines: orderLines.filter((line) => line.bookingItemId === car.bookingItemId),
          withdrawnOrderLines: withdrawnOrderLines.filter((line) => line.bookingItemId === car.bookingItemId)
        }));
  const assignedLineIds = new Set(groupedCars.flatMap((group) => group.orderLines.map((line) => line.id)));
  const otherOrderLines =
    onlyCar != null && bookingItemsCar.length === 1 ? [] : orderLines.filter((line) => !assignedLineIds.has(line.id));

  const renderOrderLine = (line: OrderLineRecordCustomer) => {
    const discounts = discountsByLine.get(line.id);
    const hasDiscounts = Boolean(discounts && discounts.length > 0);
    return (
      <div key={line.id} className='flex w-full flex-col gap-0.5'>
        <div className='flex w-full justify-between gap-4'>
          <div className={cn('flex flex-col gap-0.5', line.isUnableToComplete && 'line-through')}>
            <span>{line.description}</span>
            <span className='text-xs text-muted-foreground'>
              {line.quantity}x {formatCurrencyAmount({ money: line.amountGrossPerUnit, decimals: 2 })}
            </span>
          </div>
          <OrderLinePrice
            price={line.amountGross}
            lineTotalGross={line.lineTotalGross}
            hasDiscounts={hasDiscounts}
            className={cn('shrink-0', line.isUnableToComplete && 'line-through')}
          />
        </div>
        {hasDiscounts && discounts && (
          <DiscountLines discounts={discounts} getImageUrl={(name) => imageByDiscountName.get(name)} />
        )}
      </div>
    );
  };

  return (
    <div className='flex flex-col gap-3 text-sm'>
      <p className='text-base font-semibold'>
        <Trans>Vehicle and services</Trans>
      </p>

      {isInvoice && (
        <NoddiInfoBanner variant='info' title={t`Invoice payment`} texts={[{ value: invoiceBannerText }]} />
      )}

      {groupedCars.map(({ car, orderLines: carLines, withdrawnOrderLines: carWithdrawnLines }) => (
        <div key={car.id} className='flex flex-col gap-2 border-b pb-4'>
          <div className='flex items-center gap-2'>
            <p className='font-semibold'>{getCarDisplayName({ car, showLicensePlate: false, showYear: false })}</p>
            <p className='text-muted-foreground'>{formatLicensePlate(car.licensePlate.number)}</p>
          </div>
          {carLines.map((line) => renderOrderLine(line))}
          {carWithdrawnLines.map((line) => {
            const refunded = formatCurrencyAmount({ money: line.amountRefunded, decimals: 2 });
            return (
              <div key={line.id} className='flex w-full justify-between gap-4 text-muted-foreground'>
                <div className='flex flex-col'>
                  <span className='line-through'>{line.description}</span>
                  <span className='text-xs'>
                    <Trans>Removed from the booking</Trans>
                    {line.amountRefunded.amount > 0 && <> · {t`${refunded} refunded`}</>}
                  </span>
                </div>
                <span className='line-through'>{formatCurrencyAmount({ money: line.amountGross })}</span>
              </div>
            );
          })}
        </div>
      ))}

      {otherOrderLines.length > 0 && (
        <div className='flex flex-col gap-2 border-b pb-4'>{otherOrderLines.map((line) => renderOrderLine(line))}</div>
      )}

      <div className='flex flex-col gap-1.5 pt-1'>
        {deliveryWindowFee.amount > 0 && (
          <div className='flex justify-between gap-4'>
            <p>
              <Trans>Delivery</Trans>
            </p>
            <p>{formatCurrencyAmount({ money: deliveryWindowFee })}</p>
          </div>
        )}
        <div className='flex justify-between gap-2'>
          <p>
            <Trans>VAT</Trans>
          </p>
          <p>
            {formatCurrencyAmount({
              money: {
                amount: order.amountVat.amount,
                currency: order.currency
              }
            })}
          </p>
        </div>
        <div
          className={cn('flex justify-between gap-2', !hasPartialPayment && !isRefundDue && 'text-base font-semibold')}
        >
          <p>
            <Trans>Total sum including VAT</Trans>
          </p>
          <p>
            {formatCurrencyAmount({
              money: {
                amount: order.amountGross.amount,
                currency: order.currency
              }
            })}
          </p>
        </div>
        {(hasPartialPayment || isRefundDue) && (
          <div className='flex justify-between gap-2'>
            <p>
              <Trans>Paid</Trans>
            </p>
            <p>{formatCurrencyAmount({ money: order.amountPaid })}</p>
          </div>
        )}
        {isRefundDue && (
          <div className='mt-1 flex justify-between gap-2 border-t pt-2.5 text-base font-semibold'>
            <p>
              <Trans>To be refunded to you</Trans>
            </p>
            <p>
              {formatCurrencyAmount({
                money: {
                  amount: -order.amountOutstanding.amount,
                  currency: order.currency
                }
              })}
            </p>
          </div>
        )}
        {hasPartialPayment && (
          <div className='mt-1 flex justify-between gap-2 border-t pt-2.5 text-base font-semibold'>
            <p>
              <Trans>Total left to pay</Trans>
            </p>
            <p>
              {formatCurrencyAmount({
                money: {
                  amount: order.amountOutstanding.amount,
                  currency: order.currency
                }
              })}
            </p>
          </div>
        )}
      </div>
      {isJobCompletedAndPaymentLeft && (
        <InitiatePaymentSessionButton booking={booking} className='mt-2 w-full md:w-fit' ref={paymentButtonRef} />
      )}

      {isPayAfterAwaitingCompletion && (
        <NoddiInfoBanner
          variant='info'
          title={t`Pay after the service`}
          texts={[{ value: t`You can pay here once the service has been carried out. Nothing to do right now.` }]}
        />
      )}
    </div>
  );
};
